import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, DestroyRef, ElementRef, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ActivatedRoute } from '@angular/router';
import { isEmpty } from 'lodash';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { RegexConstants } from 'src/app/shared/constants/regex.constants';
import { ToastLevel } from 'src/app/shared/models/toast-message.module';
import { ToastService } from 'src/app/shared/services/toast.service';

import { EmailService } from '../../../../core/api/email.service';
import { SiteService } from '../../../../core/api/site.service';
import { UserService } from '../../../../core/api/user.service';
import { Site, User } from '../../../../shared/models';
import { BackService } from '../../../../shared/services/back.service';
import { SpinnerService } from '../../../../shared/spinner/spinner.service';

@Component({
  selector: 'fc-send-email',
  templateUrl: './send-email.component.html',
  styleUrls: ['./send-email.component.scss'],
})
export class SendEmailComponent {
  destroyRef = inject(DestroyRef);
  allRecipients: User[] = [];
  attachments: File[] = [];
  editor = Editor;
  emailForm: FormGroup;
  filteredRecipients: User[] = [];
  recipients: User[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];
  site: Site;
  user: User;

  @ViewChild('documentUpload', { static: false }) documentUpload?: ElementRef;
  @ViewChild('recipientInput') recipientInput: ElementRef<HTMLInputElement>;

  constructor(
    readonly backService: BackService,
    readonly emailService: EmailService,
    readonly formBuilder: FormBuilder,
    readonly siteService: SiteService,
    readonly userService: UserService,
    readonly route: ActivatedRoute,
    readonly spinnerService: SpinnerService,
    readonly toastService: ToastService
  ) {
    this.emailForm = this.formBuilder.group({
      body: new UntypedFormControl(null, [Validators.required]),
      recipients: new UntypedFormControl([], [Validators.required]),
      subject: new UntypedFormControl(null, [Validators.required]),
    });
    this.route.queryParamMap
      .pipe(
        map(queryParams => ({
          siteId: queryParams.get('siteId'),
          userId: queryParams.get('userId'),
        })),
        switchMap(params => {
          const { siteId, userId } = params;
          if (siteId) {
            return this.siteService.getSite(parseInt(siteId, 10)).pipe(
              map(site => ({ type: 'site', data: site })),
              catchError(err => {
                console.log(`Error fetching site: ${err}`);
                return of(null);
              })
            );
          } else if (userId) {
            return this.userService.getUser(parseInt(userId, 10)).pipe(
              map(user => ({ type: 'user', data: user })),
              catchError(err => {
                console.log(`Error fetching user: ${err}`);
                return of(null);
              })
            );
          } else {
            return of(null);
          }
        }),
        tap(result => {
          if (result) {
            if (result.type === 'site') {
              this.site = result.data;
              this.setRecipients(this.site.account.user);
            } else if (result.type === 'user') {
              this.user = result.data;
              this.setRecipients(this.user);
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: e => {
          console.log(`Error when initializing recipients: ${e}`);
        },
      });
  }

  sendEmail(): void {
    const formData = new FormData();
    const formValue = this.emailForm.value;
    formData.append('body', formValue.body);

    if (!isEmpty(this.emailForm.controls.recipients.value)) {
      formData.append(
        'recipients',
        JSON.stringify(
          this.emailForm.controls.recipients.value.map(r => ({
            id: r.id,
            email: r.email,
            first_name: r.firstName,
            last_name: r.lastName,
          }))
        )
      );
    }
    formData.append('subject', formValue.subject);
    this.attachments.forEach((file, index) => {
      formData.append(`attachments[${index}]`, file, file.name);
    });
    this.emailService.sendEmail(formData).subscribe({
      next: () => {
        this.toastService.sendMessage('Email(s) sent successfully', ToastLevel.SUCCESS);
        this.backService.goBack();
      },
      error: err => {
        this.toastService.sendMessage(`Error sending email: ${err}`, ToastLevel.WARN);
      },
    });
  }

  setRecipients(user: User) {
    this.allRecipients = [user];
    this.recipients.push(user);
    if (user.delegate) {
      this.recipients.push(user.delegate);
      this.allRecipients.push(user.delegate);
    }
    this.emailForm.controls.recipients.setValue(this.recipients);
    this.filteredRecipients = this.allRecipients;
  }

  removeRecipient(recipient: User): void {
    const index = this.recipients.indexOf(recipient);
    if (index >= 0) {
      this.recipients.splice(index, 1);
      this.emailForm.controls.recipients.setValue(this.recipients);
    }
  }

  selectRecipient(event: MatAutocompleteSelectedEvent): void {
    const selectedUser = event.option.value as User;
    if (!this.recipients.find(r => r.email === selectedUser.email.toLowerCase())) {
      this.recipients.push(selectedUser);
      this.emailForm.controls.recipients.setValue(this.recipients);
    }
    this.filteredRecipients = this.filteredRecipients.filter(r => r !== selectedUser);
    this.recipientInput.nativeElement.value = '';
  }

  recipientInputOnBlur($event): void {
    // needed if the user tabs off the input or clicks outside of it after typing a valid email
    const value = $event.target.value.toLowerCase().trim();
    // need to first check if that email is in the list of all recipients
    // then we check if it is a valid email
    if (RegexConstants.EMAIL.test(value) && !this.recipients.find(r => r.email === value)) {
      const user = new User({
        email: value,
        firstName: null,
        id: null,
        lastName: null,
      });
      this.recipients.push(user);
      this.emailForm.controls.recipients.setValue(this.recipients);
      this.recipientInput.nativeElement.value = '';
    }
  }

  filterRecipients($event: Event): void {
    // needed to debounce the synchoronous event
    setTimeout(() => {
      const value = ($event.target as HTMLInputElement).value;
      const filterValue = value.toLowerCase().trim();

      if (!filterValue || filterValue === '') {
        this.filteredRecipients = this.allRecipients;
      } else {
        this.filteredRecipients = this.allRecipients.filter(
          recipient =>
            recipient.email.toLowerCase().includes(filterValue) ||
            recipient.firstName?.toLowerCase().includes(filterValue) ||
            recipient.lastName?.toLowerCase().includes(filterValue)
        );
        // if the filter value is an email and not already in the list, add it
        if (RegexConstants.EMAIL.test(filterValue) && !this.filteredRecipients.find(fr => fr.email === filterValue)) {
          const user = new User({
            email: filterValue,
            firstName: null,
            id: null,
            lastName: null,
          });
          this.filteredRecipients.push(user);
        }
      }
    }, 500);
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
  }

  browseFiles(): void {
    if (this.documentUpload) {
      const documentUpload = this.documentUpload.nativeElement;
      if (documentUpload) {
        documentUpload.onchange = (): void => {
          if (this.documentUpload && documentUpload.files.length > 0) {
            for (const file of documentUpload.files) {
              this.attachments.push(file);
            }
            this.documentUpload.nativeElement.value = '';
          }
        };
        documentUpload.click();
      }
    }
  }
}
